import type { SVGProps } from 'react';
import { cn } from '~/lib/utils';
import React from 'react';

interface LogoIconProps {
  SVGAttributes?: SVGProps<SVGSVGElement>;
  title?: string;
  className?: string;
}

export function LogoIcon({
  SVGAttributes = { role: 'img', 'aria-label': 'Fireworks AI Home' },
  className,
  title,
}: LogoIconProps) {
  return (
    <svg {...SVGAttributes} viewBox="0 0 638 315" xmlns="http://www.w3.org/2000/svg">
      {title && <title>{title}</title>}
      <path
        d="M318.563 221.755C300.863 221.755 284.979 211.247 278.206 194.978L196.549 0H244.342L318.842 178.361L393.273 0H441.066L358.92 195.048C352.112 211.247 336.263 221.755 318.563 221.755Z"
        className={cn(process.env.NODE_ENV !== 'development' ? 'fill-primary' : 'fill-secondary', className)}
      />
      <path
        d="M425.111 314.933C407.481 314.933 391.667 304.494 384.824 288.366C377.947 272.097 381.507 253.524 393.936 240.921L542.657 90.2803L561.229 134.094L425.076 271.748L619.147 270.666L637.72 314.479L425.146 315.003L425.076 314.933H425.111Z"
        className={cn(process.env.NODE_ENV !== 'development' ? 'fill-primary' : 'fill-secondary', className)}
      />
      <path
        d="M0 314.408L18.5727 270.595L212.643 271.677L76.525 133.988L95.0977 90.1748L243.819 240.816C256.247 253.384 259.843 272.026 252.93 288.26C246.088 304.424 230.203 314.827 212.643 314.827L0.0698221 314.339L0 314.408Z"
        className={cn(process.env.NODE_ENV !== 'development' ? 'fill-primary' : 'fill-secondary', className)}
      />
    </svg>
  );
}

interface LogoProps extends LogoIconProps {
  logoColor?: string;
}

export const Logo = ({
  SVGAttributes = { role: 'img', 'aria-label': 'Fireworks AI Home' },
  title,
  className,
  logoColor = process.env.NODE_ENV !== 'development' ? 'fill-primary' : 'fill-secondary',
}: LogoProps) => {
  return (
    <svg {...SVGAttributes} width="130" viewBox="0 0 835 91" xmlns="http://www.w3.org/2000/svg">
      {title && <title>{title}</title>}
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M112.65 0L91.33 51.09L69.99 0H56.3L79.69 55.85C81.63 60.51 86.18 63.52 91.25 63.52C96.32 63.52 100.86 60.51 102.81 55.87L126.34 0H112.65ZM121.76 77.84L160.76 38.41L155.44 25.86L112.84 69.01C109.28 72.62 108.26 77.94 110.23 82.6C112.19 87.22 116.72 90.21 121.77 90.21L121.79 90.23L182.68 90.08L177.36 77.53L121.77 77.84H121.76ZM21.92 38.38L27.24 25.83L69.84 68.98C73.4 72.58 74.43 77.92 72.45 82.57C70.49 87.2 65.94 90.18 60.91 90.18L0.02 90.04L0 90.06L5.32 77.51L60.91 77.82L21.92 38.38Z"
        className={logoColor}
      />
      <path
        d="M231.32 85.2198L231.33 85.2298H241.8V49.1698H275.62V39.8198H241.8V16.3598H279V7.00977H231.32V85.2198Z"
        className={cn('fill-black dark:fill-white', className)}
      />
      <path d="M299.68 28.73H289.86V85.22H299.68V28.73Z" className={cn('fill-black dark:fill-white', className)} />
      <path
        d="M324.58 36.2198H324.59C324.37 36.7598 324.16 37.0898 323.5 37.0898C322.95 37.0898 322.74 36.8798 322.74 36.3398V28.7298H312.92V85.2198H322.72V53.1598C322.72 42.3098 327.75 38.0698 337.24 38.0698H345.1V28.5098H338.77C331.03 28.5098 327.1 30.7898 324.58 36.2198Z"
        className={cn('fill-black dark:fill-white', className)}
      />
      <path
        d="M377.76 78.3996C367.23 78.3996 359.37 72.4196 358.71 59.7196H404.6V54.2796C404.6 38.5296 395 27.1196 377.53 27.1196C360.06 27.1196 348.93 38.5296 348.93 56.9896C348.93 75.4496 359.73 86.8596 377.74 86.8596C395.75 86.8596 403.15 75.8996 404.81 67.3196H394.57C392.98 73.7396 388.29 78.3996 377.76 78.3996ZM377.53 35.5696C387.91 35.5696 394.33 41.1196 394.78 51.5496H358.98C360.61 40.8896 368.14 35.5696 377.53 35.5696Z"
        className={cn('fill-black dark:fill-white', className)}
      />
      <path
        d="M474.29 74.68C474.05 75.66 473.75 75.99 472.97 75.99C472.19 75.99 471.86 75.66 471.65 74.68L460.73 28.73H443.81L432.89 74.68C432.65 75.66 432.35 75.99 431.57 75.99C430.79 75.99 430.46 75.66 430.25 74.68L419.33 28.73H409.73V30.91H409.79L423.11 85.22H439.97L451.22 37.85C451.43 37.08 451.64 36.87 452.3 36.87C452.84 36.87 453.17 37.1 453.38 37.85L464.63 85.22H481.49L494.81 30.91V28.73H485.21L474.29 74.68Z"
        className={cn('fill-black dark:fill-white', className)}
      />
      <path
        d="M529.05 27.1099C512.56 27.1099 499.47 37.4199 499.47 56.9799C499.47 76.5399 512.55 86.8499 529.05 86.8499C545.55 86.8499 558.64 76.5399 558.64 56.9799C558.64 37.4199 545.54 27.1099 529.05 27.1099ZM529.07 78.1599C517.61 78.1599 509.42 70.5699 509.42 56.9799C509.42 43.3899 517.61 35.7999 529.07 35.7999C540.53 35.7999 548.72 43.4099 548.72 56.9799C548.72 70.5499 540.53 78.1599 529.07 78.1599Z"
        className={cn('fill-black dark:fill-white', className)}
      />
      <path
        d="M580.68 36.2198C580.47 36.7598 580.26 37.0898 579.6 37.0898C579.05 37.0898 578.841 36.8798 578.841 36.3398V28.7298H569.021V85.2098H578.82V53.1598C578.82 42.3098 583.851 38.0698 593.341 38.0698H601.201V28.5098H594.87C587.13 28.5098 583.2 30.7898 580.68 36.2198Z"
        className={cn('fill-black dark:fill-white', className)}
      />
      <path
        d="M618.591 55.0198V7.00977H608.771V85.2698H618.591V67.2298L629.24 58.1498L650.42 85.2498H661.16V83.0698L636.49 51.9398L661.16 30.9098V28.7298H648.54L618.591 55.0198Z"
        className={cn('fill-black dark:fill-white', className)}
      />
      <path
        d="M695.19 52.8899L687.12 51.3699C679.38 49.8999 675.99 48.2799 675.99 43.5999C675.99 38.9199 679.82 35.4499 688.98 35.4499C698.14 35.4499 703.38 38.9399 704.14 46.6499H714.14C713.03 32.8799 702.34 27.1299 688.94 27.1299C675.54 27.1299 666.13 32.8899 666.13 43.7399C666.13 54.5899 673.83 58.3499 684.91 60.4099L692.98 61.9299C700.84 63.3999 704.77 65.0899 704.77 69.9699C704.77 74.8499 700.83 78.4899 691.35 78.4899C681.87 78.4899 675.58 74.5799 674.82 67.0799H664.83C665.76 80.5499 676.73 86.8499 691.36 86.8499C705.99 86.8499 714.61 80.6099 714.61 69.4099C714.61 58.2099 705.55 54.8399 695.19 52.8899Z"
        className={cn('fill-black dark:fill-white', className)}
      />
      <path
        d="M834.64 7.00977H823.63V85.2698H834.64V7.00977Z"
        className={cn('fill-black dark:fill-white', className)}
      />
      <path
        d="M770.23 7.77L739.71 83.8398V85.2698H750.61L758.34 64.8398H795.08L802.81 85.2698H814.04V83.8598L783.3 7.00977H770.23ZM761.97 55.3798L775.09 21.0098H775.08C775.3 20.4198 775.87 20.0298 776.5 20.0298H777.04C777.67 20.0298 778.24 20.4198 778.46 21.0098L791.48 55.3798H761.97Z"
        className={cn('fill-black dark:fill-white', className)}
      />
      <path
        d="M299.68 7.00977H289.86V18.5298H299.68V7.00977Z"
        className={cn('fill-black dark:fill-white', className)}
      />
    </svg>
  );
};
